
import CancelIcon from '@material-ui/icons/Cancel';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { Box, Grid, makeStyles, Typography } from "@material-ui/core";
import { PhotoResultStatus, VerificationCheck, InspectionPhoto, InspectionDetails } from '../types/types';
import { useStore } from '@roc/client-portal-shared/hooks';
import { useMemo } from 'react';
import { Button, ConfirmationButton, Tooltip } from '@roc/ui';
import { observer } from 'mobx-react';

interface ResultsAnalysisDataProps {
  inspectionPhoto: InspectionPhoto;
  inspectionDetails: InspectionDetails;
}

export const ResultsAnalysisData = observer(({ inspectionDetails, inspectionPhoto }: ResultsAnalysisDataProps) => {
  const { inspectionStore } = useStore();

  const analyzedResults = useMemo(() => {
    return baseResults(inspectionDetails).map(result => ({
      ...result,
      status: inspectionStore.checkResult(inspectionPhoto, result.key),
    }));
  }, [inspectionPhoto, inspectionPhoto?.isLocationValidManualOverride]);

  return (
    <Box display="flex" flexDirection="column" gridGap="0.5rem">
      <Typography variant="h5">Results Analysis</Typography>
      <Box display="flex" flexDirection="column" gridGap="0.5rem">
        {analyzedResults.map(item => (
          <ResultItem
            key={item.key}
            type={item.key}
            inspectionPhoto={inspectionPhoto}
            {...item}
          />
        ))}
      </Box>
    </Box>
  );
});

const useStyles = makeStyles(() => ({
  resultItem: {
    padding: "1rem",
    border: "1px solid #ddd",
    borderRadius: 8,
  },
  resultDescription: {
    display: "flex",
    justifyContent: "start",
    alignItems: "center",
    flex:1,
  },
}));

const ResultItem = observer(({ inspectionPhoto, status, title, description, type }) => {
  const classes = useStyles();
  const { inspectionStore } = useStore();
  const { inspectionPhotosStore, canApproveManually } = inspectionStore;
  const showOverride =
    canApproveManually &&
    status === PhotoResultStatus.HAS_ISSUES &&
    type === VerificationCheck.LOCATION;

  const approve = () => {
    inspectionPhotosStore.overrideLocationValidation([inspectionPhoto.inspectionPhotoId], true);
  }

  return (
    <Box className={classes.resultItem}>
      <Grid container spacing={2}>
        <Grid item xs={5}>
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            gridGap="0.5rem"
          >
            <StatusChip status={status} />
            <Typography variant="body1">{title}</Typography>
          </Box>
        </Grid>
        <Grid item className={classes.resultDescription}>
          <Typography variant="body2">{description}</Typography>
        </Grid>
        {showOverride && (
          <Grid item>
            <ConfirmationButton
              testId="approve-manuall"
              onClick={approve}
              color="primary"
              variant="outlined"
              confirmDialogProps={{
                body: `Please confirm if you’d like to manually override and approve the location this photo.`,
                confirmButtonText: 'Approve'
              }}
            >
              Approve
            </ConfirmationButton>
          </Grid>
        )}
      </Grid>
    </Box>
  );
});

const useStatusChipStyles = makeStyles(() => ({
  notApplicableStatus: {
    backgroundColor: "#BFBFBF",
    height: "30px",
    width: "30px",
    margin: "3px",
    borderRadius: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    fontSize: "0.75rem",
    lineHeight: 1,
    textAlign: "center",
    flexShrink: 0,
    flexGrow: 0,
  }
}));

const StatusChip = ({ status }) => {
  const classes = useStatusChipStyles();

  switch (status) {
    case PhotoResultStatus.OK:
      return (
        <CheckCircleIcon
          style={{
            color: "#1ACF77",
            fontSize: "2rem",
          }}
        />
      );
    case PhotoResultStatus.OVERRIDDEN:
      return (
        <Tooltip title="Manually Approved">
          <CheckCircleIcon
            style={{
              color: '#FB8C00',
              fontSize: '2rem',
            }}
          />
        </Tooltip>
      );
    case PhotoResultStatus.HAS_ISSUES:
      return (
        <CancelIcon
          style={{
            color: "#dd3030",
            fontSize: "2rem",
          }}
        />
      );
    default:
      return <Box className={classes.notApplicableStatus}>N/A</Box>;
  }
};

const baseResults = (inspectionDetails: InspectionDetails) =>  [

  {
    key: VerificationCheck.CAPTURE_INTEGRITY,
    status: PhotoResultStatus.NA,
    title: "Capture Authenticity Check",
    tooltip: "some",
    description:
      "The image has been signed & transmitted securely to our server without any edits or modifications.",
  },
  {
    key: VerificationCheck.LOCATION,
    status: PhotoResultStatus.NA,
    title: "Location Accuracy Check",
    tooltip: "some",
    description:
      `The photo was successfully verified as taken within [${inspectionDetails.allowedRadiusM}] meters of the property.`,
  },
  {
    key: VerificationCheck.TIME_DATE,
    status: PhotoResultStatus.NA,
    title: "Timestamp Check",
    tooltip: "some",
    description:
      "The photo's timestamp has been verified against the server's time and date for accuracy.",
  },
  {
    key: VerificationCheck.DATA_INTEGRITY,
    status: PhotoResultStatus.NA,
    title: "Device Authenticity Check",
    tooltip: "some",
    description:
      "The Customer's device shows no signs of being jailbroken or rooted, and has not been modified to install unauthorized software.",
  },
  {
    key: VerificationCheck.IMAGE_ANALYSIS,
    status: PhotoResultStatus.NA,
    title: "Photo Authenticity Check",
    tooltip: "some",
    description: "The photo appears to be original and does not display signs of screen re-capture or duplication.",
  },
  {
    key: VerificationCheck.REVERSE_IMAGE_SEARCH,
    status: PhotoResultStatus.NA,
    title: "Reverse Lookup Check",
    tooltip: "some",
    description: "The photo does not match or resemble any publicly available images on the internet.",
  },
];
